import React, { useState, useMemo } from 'react';
import { Card, Btn, Input, Badge } from './UI';
import { fmtNum } from '../utils';

const QSMatrixView = ({ transactions }) => {
  const [matrixViewType, setMatrixViewType] = useState('thisWeek');
  const [customDates, setCustomDates] = useState([]);
  const [selectedCustomDate, setSelectedCustomDate] = useState('');

  const activeDates = useMemo(() => {
    const today = new Date();
    const dates = [];
    
    if (matrixViewType === 'thisWeek') {
      const day = today.getDay() || 7; // Convert Sun=0 to 7
      const mon = new Date(today);
      mon.setDate(today.getDate() - day + 1);
      
      for (let i = 0; i < 7; i++) {
        const d = new Date(mon);
        d.setDate(mon.getDate() + i);
        dates.push(d.toISOString().split('T')[0]);
      }
    } else if (matrixViewType === 'lastWeek') {
      const day = today.getDay() || 7;
      const lastMon = new Date(today);
      lastMon.setDate(today.getDate() - day - 6);
      
      for (let i = 0; i < 7; i++) {
        const d = new Date(lastMon);
        d.setDate(lastMon.getDate() + i);
        dates.push(d.toISOString().split('T')[0]);
      }
    } else if (matrixViewType === 'thisMonth') {
      const year = today.getFullYear();
      const month = today.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(year, month, i);
        // adjust for timezone offset
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        dates.push(d.toISOString().split('T')[0]);
      }
    } else if (matrixViewType === 'custom') {
      return [...customDates].sort();
    }
    
    return dates;
  }, [matrixViewType, customDates]);

  const addCustomDate = () => {
    if (selectedCustomDate && !customDates.includes(selectedCustomDate)) {
      setCustomDates([...customDates, selectedCustomDate]);
    }
    setSelectedCustomDate('');
  };
  
  const removeCustomDate = (date) => {
    setCustomDates(customDates.filter(d => d !== date));
  };

  const groupedData = useMemo(() => {
    const groups = new Map();
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    
    transactions.forEach(t => {
      const boqName = t.boq_item_description || t.boq_component_name || '';
      const key = `${t.boq_item_code}_${boqName}`;
      
      if (!groups.has(key)) {
        groups.set(key, {
          boq_code: t.boq_item_code || t.boq_item_id?.substring(0,8),
          description: boqName,
          discipline: t.discipline,
          unit: t.unit_of_measure,
          boq_qty: Number(t.boq_quantity || 0),
          issued: 0,
          prev_inst: 0,
          
          // daily aggregates mapped by date
          daily: {},
          
          // summaries
          total_selected: 0,
          monthly_inst: 0,
          cumulative: 0,
          remaining: Number(t.boq_quantity || 0),
          inst_percent: 0,
          offcut: 0,
          wastage: 0,
          damaged: 0,
          returned: 0,
          lost: 0,
        });
      }
      
      const g = groups.get(key);
      const dateStr = t.installation_date;
      const qty = Number(t.current_installed_qty || 0);
      
      // Daily map
      if (dateStr) {
        if (!g.daily[dateStr]) {
          g.daily[dateStr] = { qty: 0, loc: '', comment: '' };
        }
        g.daily[dateStr].qty += qty;
        if (t.location) g.daily[dateStr].loc = t.location;
        if (t.remarks) g.daily[dateStr].comment = t.remarks;
      }
      
      // We only compute cumulative sums from ALL transactions in this list.
      // Usually, `transactions` is all available records for the project or the filter.
      // Let's assume cumulative == total installed over these transactions or the max of total_installed_qty
      // Actually `total_installed_qty` should track the cumulative. We'll take the max or just sum `current_installed_qty`.
      g.cumulative += qty;
      g.issued += Number(t.issued_qty || 0);
      g.offcut += Number(t.offcut_qty || 0);
      g.wastage += Number(t.wastage_qty || 0);
      g.damaged += Number(t.damaged_qty || 0);
      g.returned += Number(t.returned_qty || 0);
      g.lost += Number(t.lost_qty || 0);
      
      // Monthly inst (if in current month)
      if (dateStr && dateStr.startsWith(currentMonth)) {
        g.monthly_inst += qty;
      }
    });
    
    // Finalize summaries
    const result = Array.from(groups.values()).map(g => {
      // Prev Inst = Cumulative - Monthly? Or maybe just from data. 
      // We'll calculate total_selected based on activeDates
      g.total_selected = activeDates.reduce((sum, d) => sum + (g.daily[d]?.qty || 0), 0);
      g.remaining = g.boq_qty - g.cumulative;
      if (g.remaining < 0) g.remaining = 0;
      g.inst_percent = g.boq_qty > 0 ? (g.cumulative / g.boq_qty) * 100 : 0;
      return g;
    });
    
    return result;
  }, [transactions, activeDates]);

  // Format short date (e.g. "Mon 15 Jun 2026")
  const formatHeaderDate = (dateStr) => {
    const d = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[d.getDay()]} ${d.getDate()} \n ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  return (
    <Card style={{ border: '1px solid black', borderTop: '1px solid black', overflow: 'hidden', borderRadius: 8, borderTopLeftRadius: 0 }}>
      <div style={{ padding: '12px 16px', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {['thisWeek', 'lastWeek', 'thisMonth', 'custom'].map(type => (
            <Btn 
              key={type}
              variant={matrixViewType === type ? 'primary' : 'secondary'} 
              size="sm"
              onClick={() => setMatrixViewType(type)}
            >
              {type === 'thisWeek' ? 'This Week' : type === 'lastWeek' ? 'Last Week' : type === 'thisMonth' ? 'This Month' : 'Custom'}
            </Btn>
          ))}
          
          {matrixViewType === 'custom' && (
            <div style={{ display: 'flex', gap: 6, marginLeft: 16 }}>
              <Input 
                type="date" 
                value={selectedCustomDate} 
                onChange={val => setSelectedCustomDate(val)} 
                style={{ height: 32 }}
              />
              <Btn size="sm" onClick={addCustomDate} disabled={!selectedCustomDate}>Add Day</Btn>
              
              <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                {customDates.map(d => (
                  <span key={d} style={{ background: 'var(--brand-light)', padding: '4px 8px', borderRadius: 4, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {d} <span style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => removeCustomDate(d)}>×</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {activeDates.length} date(s) • {groupedData.length} BOQ items
        </div>
      </div>

      {matrixViewType === 'custom' && activeDates.length === 0 ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
          Add dates using the date picker above to build your matrix.
        </div>
      ) : (
      <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 350px)', background: 'var(--bg)' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 3, background: '#1e293b', color: 'white', fontSize: 10, textTransform: 'uppercase' }}>
            {/* Top Header Row */}
            <tr>
              <th colSpan={7} style={{ background: '#0f172a', padding: '10px 12px', textAlign: 'left', position: 'sticky', left: 0, zIndex: 4, borderRight: '1px solid #334155', borderBottom: '1px solid #334155' }}>
                <div style={{ fontSize: 12, fontWeight: 700 }}>QS Monthly Installation Measurement Sheet</div>
                <div style={{ fontSize: 9, opacity: 0.7, fontWeight: 400, marginTop: 2, textTransform: 'none' }}>Rows: {groupedData.length}</div>
              </th>
              
              {activeDates.map((d, i) => (
                <th key={d} colSpan={3} style={{ borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', whiteSpace: 'pre-line', verticalAlign: 'middle' }}>
                  {formatHeaderDate(d)}
                </th>
              ))}
              
              <th style={{ background: '#312e81', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                Total
              </th>
              <th style={{ background: '#064e3b', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                Monthly<br/>Inst
              </th>
              <th style={{ background: '#064e3b', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                Cumulative
              </th>
              <th style={{ background: '#9a3412', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                Remaining
              </th>
              <th style={{ background: '#4c1d95', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                Inst%
              </th>
              <th style={{ background: '#334155', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>Offcut</th>
              <th style={{ background: '#334155', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>Wastage</th>
              <th style={{ background: '#7f1d1d', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>Damaged</th>
              <th style={{ background: '#334155', borderBottom: '1px solid #334155', borderRight: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>Returned</th>
              <th style={{ background: '#7f1d1d', borderBottom: '1px solid #334155', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>Lost</th>
            </tr>
            
            {/* Sub Header Row */}
            <tr>
              {/* Sticky Left Headers */}
              <th style={{ width: 120, position: 'sticky', left: 0, zIndex: 4, background: '#1e293b', padding: '8px', borderRight: '1px solid #334155', borderBottom: '1px solid #334155', textAlign: 'left' }}>BOQ CODE</th>
              <th style={{ width: 200, position: 'sticky', left: 120, zIndex: 4, background: '#1e293b', padding: '8px', borderRight: '1px solid #334155', borderBottom: '1px solid #334155', textAlign: 'left' }}>DESCRIPTION</th>
              <th style={{ width: 100, position: 'sticky', left: 320, zIndex: 4, background: '#1e293b', padding: '8px', borderRight: '1px solid #334155', borderBottom: '1px solid #334155', textAlign: 'left' }}>DISCIPLINE</th>
              <th style={{ width: 60, position: 'sticky', left: 420, zIndex: 4, background: '#1e293b', padding: '8px', borderRight: '1px solid #334155', borderBottom: '1px solid #334155', textAlign: 'center' }}>UNIT</th>
              <th style={{ width: 80, position: 'sticky', left: 480, zIndex: 4, background: '#1e293b', padding: '8px', borderRight: '1px solid #334155', borderBottom: '1px solid #334155', textAlign: 'right' }}>BOQ QTY</th>
              <th style={{ width: 80, position: 'sticky', left: 560, zIndex: 4, background: '#1e293b', padding: '8px', borderRight: '1px solid #334155', borderBottom: '1px solid #334155', textAlign: 'right' }}>ISSUED</th>
              <th style={{ width: 80, position: 'sticky', left: 640, zIndex: 4, background: '#1e293b', padding: '8px', borderRight: '2px solid #0f172a', borderBottom: '1px solid #334155', textAlign: 'right' }}>PREV INST</th>

              {/* Dynamic Dates Sub Headers */}
              {activeDates.map(d => (
                <React.Fragment key={`sub_${d}`}>
                  <th style={{ background: '#f8fafc', color: 'var(--brand)', width: 60, padding: '6px', textAlign: 'center', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>QTY</th>
                  <th style={{ background: '#f8fafc', color: '#475569', width: 100, padding: '6px', textAlign: 'left', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>LOC</th>
                  <th style={{ background: '#f8fafc', color: '#8b5cf6', width: 150, padding: '6px', textAlign: 'left', borderRight: '1px solid #94a3b8', borderBottom: '1px solid #e2e8f0' }}>COMMENT</th>
                </React.Fragment>
              ))}

              {/* Summary Sub Headers */}
              <th style={{ background: '#f8fafc', color: '#312e81', width: 70, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Total</th>
              <th style={{ background: '#f8fafc', color: '#064e3b', width: 70, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Monthly</th>
              <th style={{ background: '#f8fafc', color: '#064e3b', width: 80, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Cum.</th>
              <th style={{ background: '#f8fafc', color: '#9a3412', width: 80, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Rem.</th>
              <th style={{ background: '#f8fafc', color: '#4c1d95', width: 60, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>%</th>
              <th style={{ background: '#f8fafc', color: '#475569', width: 60, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Off.</th>
              <th style={{ background: '#f8fafc', color: '#475569', width: 60, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Wst.</th>
              <th style={{ background: '#f8fafc', color: '#7f1d1d', width: 60, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Dmg.</th>
              <th style={{ background: '#f8fafc', color: '#475569', width: 60, padding: '6px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>Ret.</th>
              <th style={{ background: '#f8fafc', color: '#7f1d1d', width: 60, padding: '6px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Lost</th>
            </tr>
          </thead>
          <tbody>
            {groupedData.length === 0 ? (
              <tr><td colSpan={7 + activeDates.length * 3 + 10} style={{ padding: 32, textAlign: 'center', color: '#64748b', background: 'white' }}>No data to display in matrix view.</td></tr>
            ) : (
              groupedData.map((row, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 2, background: idx % 2 === 0 ? 'white' : '#f8fafc', padding: '8px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: 'var(--brand)' }}>
                    {row.boq_code || '—'}
                  </td>
                  <td style={{ position: 'sticky', left: 120, zIndex: 2, background: idx % 2 === 0 ? 'white' : '#f8fafc', padding: '8px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11 }}>
                    <div style={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.description}>
                      {row.description || '—'}
                    </div>
                  </td>
                  <td style={{ position: 'sticky', left: 320, zIndex: 2, background: idx % 2 === 0 ? 'white' : '#f8fafc', padding: '8px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 10 }}>
                    {row.discipline ? <Badge color="gray">{row.discipline}</Badge> : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                  <td style={{ position: 'sticky', left: 420, zIndex: 2, background: idx % 2 === 0 ? 'white' : '#f8fafc', padding: '8px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, textAlign: 'center', fontWeight: 600 }}>
                    {row.unit || '—'}
                  </td>
                  <td style={{ position: 'sticky', left: 480, zIndex: 2, background: idx % 2 === 0 ? 'white' : '#f8fafc', padding: '8px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, textAlign: 'right', fontWeight: 700 }}>
                    {fmtNum(row.boq_qty)}
                  </td>
                  <td style={{ position: 'sticky', left: 560, zIndex: 2, background: idx % 2 === 0 ? 'white' : '#f8fafc', padding: '8px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, textAlign: 'right', color: 'var(--brand)' }}>
                    {row.issued > 0 ? fmtNum(row.issued) : <span style={{ color: '#cbd5e1' }}>0</span>}
                  </td>
                  <td style={{ position: 'sticky', left: 640, zIndex: 2, background: idx % 2 === 0 ? 'white' : '#f8fafc', padding: '8px', borderRight: '2px solid #0f172a', borderBottom: '1px solid #e2e8f0', fontSize: 11, textAlign: 'right' }}>
                    {row.prev_inst > 0 ? fmtNum(row.prev_inst) : <span style={{ color: '#cbd5e1' }}>0</span>}
                  </td>

                  {/* Daily Values */}
                  {activeDates.map(d => {
                    const daily = row.daily[d];
                    const hasQty = daily && daily.qty > 0;
                    return (
                      <React.Fragment key={`cell_${d}`}>
                        <td style={{ padding: '6px', textAlign: 'center', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: hasQty ? 700 : 400, color: hasQty ? 'var(--brand)' : '#cbd5e1' }}>
                          {hasQty ? fmtNum(daily.qty) : '—'}
                        </td>
                        <td style={{ padding: '6px', textAlign: 'left', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 10, color: '#475569' }}>
                          <div style={{ maxWidth: 90, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={daily?.loc}>{daily?.loc || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
                        </td>
                        <td style={{ padding: '6px', textAlign: 'left', borderRight: '1px solid #94a3b8', borderBottom: '1px solid #e2e8f0', fontSize: 10, color: '#8b5cf6' }}>
                          <div style={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={daily?.comment}>{daily?.comment || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
                        </td>
                      </React.Fragment>
                    );
                  })}

                  {/* Summaries */}
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: '#312e81' }}>
                    {row.total_selected > 0 ? fmtNum(row.total_selected) : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, color: '#064e3b' }}>
                    {row.monthly_inst > 0 ? fmtNum(row.monthly_inst) : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: '#064e3b' }}>
                    {row.cumulative > 0 ? fmtNum(row.cumulative) : <span style={{ color: '#cbd5e1' }}>0</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: '#9a3412' }}>
                    {fmtNum(row.remaining)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: '#4c1d95' }}>
                    {row.inst_percent > 0 ? `${row.inst_percent.toFixed(1)}%` : <span style={{ color: '#cbd5e1' }}>0.0%</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: 'var(--brand)' }}>
                    {row.offcut > 0 ? fmtNum(row.offcut) : <span style={{ color: '#cbd5e1' }}>0</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: '#d97706' }}>
                    {row.wastage > 0 ? fmtNum(row.wastage) : <span style={{ color: '#cbd5e1' }}>0</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, color: '#dc2626' }}>
                    {row.damaged > 0 ? fmtNum(row.damaged) : <span style={{ color: '#cbd5e1' }}>0</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: '#475569' }}>
                    {row.returned > 0 ? fmtNum(row.returned) : <span style={{ color: '#cbd5e1' }}>0</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: '#475569' }}>
                    {row.lost > 0 ? fmtNum(row.lost) : <span style={{ color: '#cbd5e1' }}>0</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
    </Card>
  );
};

export default QSMatrixView;
